/**
 * @file Maintenance frontend API owner.
 *
 * Runtime workflows remain hidden until the Maintenance module is enabled, but
 * backend-owned save authority is exposed here for the future create/edit
 * surface.
 */

import {
  lookupMaintenanceAssetCandidatesCallable as configuredLookupMaintenanceAssetCandidatesCallable,
  saveMaintenanceItemCallable as configuredSaveMaintenanceItemCallable,
  updateMaintenanceAssetLinkCallable as configuredUpdateMaintenanceAssetLinkCallable,
} from "../../app/firebase/callables.js";

/**
 * Returns the module permission required for Maintenance workflows.
 * @returns {string} Maintenance permission key.
 */
export function getMaintenancePermissionKey() {
  return "maintenance";
}

/**
 * Returns whether Maintenance runtime workflows are exposed.
 * @returns {boolean} True when the dev Maintenance board preview is enabled.
 */
export function isMaintenanceRuntimeEnabled() {
  return true;
}

/**
 * Creates Maintenance API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Maintenance API helpers.
 */
export function createMaintenanceApi({
  lookupMaintenanceAssetCandidatesCallable,
  saveMaintenanceItemCallable,
  updateMaintenanceAssetLinkCallable,
}) {
  return {
    /**
     * Looks up active/open Asset candidates through the approved Maintenance
     * linked-parent workflow.
     * @param {object} params Lookup params.
     * @param {string} [params.search] Search text.
     * @param {Array<string>} [params.typeIds] Optional Asset type IDs.
     * @param {number} [params.limit] Result limit.
     * @returns {Promise<object>} Callable result payload.
     */
    async lookupMaintenanceAssetCandidates({
      search = "",
      typeIds = [],
      limit = 20,
    } = {}) {
      const result = await lookupMaintenanceAssetCandidatesCallable({
        search,
        typeIds,
        limit,
      });
      return result.data;
    },

    /**
     * Saves a Maintenance item through backend validation.
     * @param {object} params Save params.
     * @param {string|null} params.itemId Existing item id for updates.
     * @param {object} params.data Submitted Maintenance data.
     * @returns {Promise<object>} Callable result payload.
     */
    async saveMaintenanceItem({itemId = null, data = {}} = {}) {
      const result = await saveMaintenanceItemCallable({itemId, data});
      return result.data;
    },

    /**
     * Updates a Maintenance item's linked Asset through backend authority.
     * @param {object} params Link params.
     * @param {string} params.maintenanceId Maintenance item id.
     * @param {string|null} params.assetId Target asset id, or null to clear.
     * @param {*} [params.expectedUpdatedAt=null] Expected item update time.
     * @returns {Promise<object>} Callable result payload.
     */
    async updateMaintenanceAssetLink({
      maintenanceId,
      assetId = null,
      expectedUpdatedAt = null,
    } = {}) {
      const result = await updateMaintenanceAssetLinkCallable({
        maintenanceId,
        assetId,
        expectedUpdatedAt,
      });
      return result.data;
    },
  };
}

const configuredMaintenanceApi = createMaintenanceApi({
  lookupMaintenanceAssetCandidatesCallable:
    configuredLookupMaintenanceAssetCandidatesCallable,
  saveMaintenanceItemCallable: configuredSaveMaintenanceItemCallable,
  updateMaintenanceAssetLinkCallable:
    configuredUpdateMaintenanceAssetLinkCallable,
});

export const lookupMaintenanceAssetCandidates =
  configuredMaintenanceApi.lookupMaintenanceAssetCandidates;
export const saveMaintenanceItem =
  configuredMaintenanceApi.saveMaintenanceItem;
export const updateMaintenanceAssetLink =
  configuredMaintenanceApi.updateMaintenanceAssetLink;
